import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, IconButton, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import {
  getClientUsers, setClientUserRole, inviteClientUser, createClientUser, removeClientUser, revokeClientInvite,
  type ClientUser,
} from '@/data/client';
import { apiErrMsg } from './util';

const ROLE_OPTIONS: { value: ClientUser['role']; label: string }[] = [
  { value: 'client_manager', label: 'Client Manager' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'expert', label: 'Expert Studio only' },
];

/* Who can sign in to this client, with what role, and two ways to add someone.

   Invite is the default: Clerk emails a link, the person picks their own
   password, and no secret ever passes through Triton. Create-with-password is
   for the clients whose mail security mangles invite links (Outlook Safe Links,
   most often) — the admin creates the account and hands over a temporary
   password out of band. That password is shown ONCE, here, and nowhere else. */
export function ClientUsersEditor({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['clientUsers', orgId], queryFn: () => getClientUsers(orgId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['clientUsers', orgId] });
  const users = data?.users ?? [];
  const invites = data?.invites ?? [];

  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id); setRowErr(null);
    try { await fn(); refresh(); }
    catch (e) { setRowErr(apiErrMsg(e, 'That did not work.')); }
    finally { setBusyId(null); }
  };

  // ---- add someone
  const [mode, setMode] = useState<'invite' | 'create'>('invite');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<ClientUser['role']>('client_manager');
  const [password, setPassword] = useState('');
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [addMsg, setAddMsg] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => { setEmail(''); setFirstName(''); setLastName(''); setPassword(''); };
  const add = async () => {
    setAdding(true); setAddErr(null); setAddMsg(null); setSecret(null);
    try {
      if (mode === 'invite') {
        const r = await inviteClientUser(orgId, { email, role });
        setAddMsg(`Invitation sent to ${r.email}. They pick their own password from the link.`);
      } else {
        const r = await createClientUser(orgId, { email, firstName, lastName, role, ...(password ? { password } : {}) });
        if (r.temporaryPassword) setSecret({ email: r.email, password: r.temporaryPassword });
        else setAddMsg(`Account created for ${r.email} with the password you entered.`);
      }
      resetForm();
      refresh();
    } catch (e) {
      setAddErr(apiErrMsg(e, 'Could not add this person.'));
    } finally {
      setAdding(false);
    }
  };
  const copy = async () => {
    if (!secret) return;
    try { await navigator.clipboard.writeText(secret.password); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };

  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Client users &amp; roles</h4>
      <p className="dash-muted admin-sect__note">
        <strong>Client Manager</strong> = all reports + contentIQ + Expert Studio. <strong>Contributor</strong> = contentIQ + Expert Studio, no reports. <strong>Expert Studio only</strong> = just Expert Studio (jobs, galleries, interviews) — no reports, no contentIQ. Expert Studio shows for every role when it&rsquo;s enabled for the client.
      </p>

      {isLoading ? (
        <p className="dash-muted">Loading users…</p>
      ) : isError ? (
        /* A failed load used to render as "No client users yet", which sent
           people hunting for members that were there all along. */
        <p className="admin-modal__err">Couldn’t load this client’s users: {apiErrMsg(error, 'request failed')}</p>
      ) : users.length === 0 && invites.length === 0 ? (
        <p className="dash-muted">No client users yet — add the first one below.</p>
      ) : (
        <div className="admin-users">
          {users.map((u) => (
            <div key={u.userId} className="admin-users__row">
              <div className="admin-users__who"><strong>{u.name}</strong><span>{u.email}</span></div>
              <select className="tr-select" value={u.role} disabled={busyId === u.userId}
                onChange={(e) => act(u.userId, () => setClientUserRole(orgId, u.userId, e.target.value))}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <IconButton label={`Remove ${u.name} from this client`} variant="ghost" disabled={busyId === u.userId}
                onClick={() => { if (window.confirm(`Remove ${u.name} (${u.email}) from this client? Their account is kept; they just lose access here.`)) act(u.userId, () => removeClientUser(orgId, u.userId)); }}>
                <Icon name="trash" size={15} />
              </IconButton>
            </div>
          ))}
          {invites.map((i) => (
            <div key={i.email} className="admin-users__row admin-users__pending">
              <div className="admin-users__who"><strong>{i.email}</strong><span>{i.invitedAt.slice(0, 10)} · {ROLE_OPTIONS.find((o) => o.value === i.role)?.label ?? i.role}</span></div>
              <Button variant="ghost" size="sm" disabled={busyId === i.email}
                onClick={() => { if (window.confirm(`Revoke the invitation to ${i.email}?`)) act(i.email, () => revokeClientInvite(orgId, i.email)); }}>
                Revoke
              </Button>
            </div>
          ))}
        </div>
      )}
      {rowErr && <p className="admin-modal__err" style={{ marginTop: 8 }}>{rowErr}</p>}

      <div className="admin-adduser">
        <div className="admin-adduser__mode" role="tablist">
          <button type="button" role="tab" className={mode === 'invite' ? 'is-on' : ''} onClick={() => setMode('invite')}>Invite by email</button>
          <button type="button" role="tab" className={mode === 'create' ? 'is-on' : ''} onClick={() => setMode('create')}>Create with password</button>
        </div>
        <p className="dash-muted admin-sect__note" style={{ marginTop: 0 }}>
          {mode === 'invite'
            ? 'They get an email from Clerk and choose their own password. Use this unless their email security breaks the link.'
            : 'For clients whose mail security breaks invite links. You hand them the temporary password another way, and it is shown here exactly once.'}
        </p>
        <div className="admin-adduser__row">
          {mode === 'create' && (
            <>
              <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </>
          )}
          <Input label="Email" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <label className="tr-field">
            <span className="tr-field__label">Role</span>
            <div className="tr-field__control">
              <select className="tr-select" value={role} onChange={(e) => setRole(e.target.value as ClientUser['role'])}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </label>
          {mode === 'create' && (
            <Input label="Temporary password" hint="Leave blank to generate a strong one." type="text" autoComplete="off"
              value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </div>
        <div className="admin-adduser__actions">
          <Button variant="primary" size="sm" disabled={adding || !email.trim()} onClick={add}
            iconLeft={<Icon name={mode === 'invite' ? 'mail' : 'user-plus'} size={14} />}>
            {adding ? (mode === 'invite' ? 'Sending…' : 'Creating…') : (mode === 'invite' ? 'Send invitation' : 'Create account')}
          </Button>
          {addErr && <span className="admin-modal__err">{addErr}</span>}
          {addMsg && <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>{addMsg}</span>}
        </div>
        {secret && (
          <div className="admin-secret" role="status">
            <strong>Account created for {secret.email}.</strong> Temporary password, shown once:
            <div className="admin-secret__pw">
              <code>{secret.password}</code>
              <Button variant="outline" size="sm" onClick={copy} iconLeft={<Icon name={copied ? 'check' : 'copy'} size={14} />}>{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <p>Send it to them by a different channel than email if you can, and ask them to change it after their first sign-in. It is not stored anywhere — closing this page loses it.</p>
          </div>
        )}
      </div>
    </section>
  );
}
